import { api } from './client'

export type StoryStatus = 'todo' | 'in_progress' | 'in_review' | 'done'
export type StoryPriority = 'low' | 'med' | 'high'
export type StoryType = 'story' | 'bug' | 'task'

export type RecurrenceRule =
  | { type: 'daily' }
  | { type: 'every_n_days'; n: number }
  | { type: 'weekly'; days: string[] }
  | { type: 'every_n_weeks'; n: number; days: string[] }
  | { type: 'monthly'; day_of_month: number }
  | { type: 'yearly'; month: number; day_of_month: number }

export type Story = {
  id: number
  project_id: number
  project_key: string | null
  epic_id: number | null
  epic_name: string | null
  epic_color: string | null
  sprint_id: number | null
  sprint_name: string | null
  key: string
  type: StoryType
  title: string
  description: string | null
  status_id: number
  status_name: string
  status_category: StoryStatus
  status_color: string | null
  priority: StoryPriority
  assignee_id: number | null
  assignee_name: string | null
  assignee_email: string | null
  assignee_avatar_color: string | null
  reporter_id: number | null
  estimate_points: number | null
  estimate_minutes: number | null
  scheduled_start: string | null
  scheduled_end: string | null
  due_date: string | null
  /** Timeline plan: planned_start → due_date. */
  planned_start?: string | null
  /** Set by the API the first time the story moves into progress; editable. */
  actual_start?: string | null
  labels: string[]
  sort_order: number
  completed_at: string | null
  created_at: string
  updated_at: string
  // Recurring (P12b)
  is_recurring?: boolean
  recurrence_rule?: RecurrenceRule | null
  recurrence_trigger?: 'start_date' | 'on_completion' | null
  parent_story_id?: number | null
  recurrence_end_date?: string | null
}

export type StoryDetail = Story & {
  subtasks: Subtask[]
  blocks: DepRow[]
  blocked_by: DepRow[]
  /** Calendar work sessions, earliest first (show only). */
  sessions?: StorySession[]
}

/**
 * One scheduled block of work on a story. A story that takes more than one
 * sitting has several; `scheduled_start`/`scheduled_end` on the story are
 * their span (first start → last end).
 */
export type StorySession = {
  id: number
  story_id: number
  start_at: string
  end_at: string
  created_by: number | null
  created_at: string
  updated_at: string
}

export async function listStorySessions(storyId: number): Promise<StorySession[]> {
  return api<StorySession[]>(`/stories/${storyId}/sessions`)
}
export async function addStorySession(storyId: number, win: { start_at: string; end_at: string }): Promise<StorySession> {
  return api<StorySession>(`/stories/${storyId}/sessions`, { method: 'POST', body: win })
}
export async function updateStorySession(storyId: number, id: number, win: { start_at?: string; end_at?: string }): Promise<StorySession> {
  return api<StorySession>(`/stories/${storyId}/sessions/${id}`, { method: 'PATCH', body: win })
}
export async function deleteStorySession(storyId: number, id: number): Promise<void> {
  return api<void>(`/stories/${storyId}/sessions/${id}`, { method: 'DELETE' })
}

export type Subtask = {
  id: number
  story_id: number
  title: string
  done: boolean
  assignee_id: number | null
  sort_order: number
  completed_at: string | null
  created_at: string
  updated_at: string
}

export type DepRow = {
  id: number
  key: string
  title: string
  status_category: string
  kind: 'blocks' | 'relates' | 'duplicates'
}

export type StoryListResponse = {
  data: Story[]
  meta?: { pagination: { page: number; per_page: number; total: number; total_pages: number } }
}

export type StoryFilters = {
  project_id?: number
  epic_id?: number
  sprint_id?: number
  assignee_id?: number
  status_id?: number
  priority?: StoryPriority
  type?: StoryType
  q?: string
  sort?: string
  page?: number
  per_page?: number
}

export async function listStories(filters: StoryFilters): Promise<StoryListResponse> {
  // api() unwraps {data: …} envelope. Pagination meta is dropped — we re-shape here.
  // To preserve meta, use a raw fetch instead.
  const url = new URL('/api/v1/stories', window.location.origin)
  for (const [k, v] of Object.entries(filters)) {
    if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, String(v))
  }
  const token = localStorage.getItem('atlas_token')
  const res = await fetch(url.toString(), {
    headers: { Authorization: `Bearer ${token}` },
  })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return (await res.json()) as StoryListResponse
}

export async function getStory(idOrKey: string | number): Promise<StoryDetail> {
  return api<StoryDetail>(`/stories/${idOrKey}`)
}

export type StoryUpdate = Partial<{
  title: string
  description: string | null
  epic_id: number | null
  sprint_id: number | null
  type: StoryType
  priority: StoryPriority
  status_id: number
  assignee_id: number | null
  estimate_points: number | null
  estimate_minutes: number | null
  due_date: string | null
  planned_start: string | null
  actual_start: string | null
  scheduled_start: string | null
  scheduled_end: string | null
  is_recurring: 0 | 1
  recurrence_rule: RecurrenceRule | null
  recurrence_trigger: 'start_date' | 'on_completion' | null
  recurrence_end_date: string | null
}>

export async function updateStory(idOrKey: string | number, patch: StoryUpdate): Promise<Story> {
  return api<Story>(`/stories/${idOrKey}`, { method: 'PATCH', body: patch })
}

export async function createStory(data: Partial<Story> & { project_id: number; title: string }): Promise<Story> {
  return api<Story>('/stories', { method: 'POST', body: data })
}

export async function deleteStory(id: number | string): Promise<void> {
  return api<void>(`/stories/${id}`, { method: 'DELETE' })
}

// ─── Subtasks ────────────────────────────────────────────────────────────────

export async function listSubtasks(storyId: number): Promise<Subtask[]> {
  return api<Subtask[]>(`/stories/${storyId}/subtasks`)
}

export async function addSubtask(storyId: number, title: string): Promise<Subtask> {
  return api<Subtask>(`/stories/${storyId}/subtasks`, { method: 'POST', body: { title } })
}

export async function updateSubtask(storyId: number, id: number, patch: Partial<Subtask>): Promise<Subtask> {
  return api<Subtask>(`/stories/${storyId}/subtasks/${id}`, { method: 'PATCH', body: patch })
}

export async function deleteSubtask(storyId: number, id: number): Promise<void> {
  return api<void>(`/stories/${storyId}/subtasks/${id}`, { method: 'DELETE' })
}

// ─── Comments ────────────────────────────────────────────────────────────────

export type Comment = {
  id: number
  parent_type: 'story'
  parent_id: number
  user_id: number
  user_name: string | null
  user_email: string | null
  user_avatar_color: string | null
  body: string
  /**
   * Comments written before markdown support are 'plain' and must keep
   * rendering verbatim — a stray `*` in an old comment is not emphasis.
   */
  body_format: 'plain' | 'markdown'
  created_at: string
  updated_at: string
}

export async function listComments(storyId: number): Promise<Comment[]> {
  return api<Comment[]>(`/stories/${storyId}/comments`)
}

export async function addComment(storyId: number, body: string): Promise<Comment> {
  return api<Comment>(`/stories/${storyId}/comments`, {
    method: 'POST',
    body: { body, body_format: 'markdown' },
  })
}

export async function updateComment(commentId: number, body: string): Promise<Comment> {
  return api<Comment>(`/comments/${commentId}`, {
    method: 'PATCH',
    body: { body, body_format: 'markdown' },
  })
}

export async function deleteComment(id: number): Promise<void> {
  return api<void>(`/comments/${id}`, { method: 'DELETE' })
}

// ─── Workflow states (for status picker) ─────────────────────────────────────

export type WorkflowState = {
  id: number
  project_id: number
  name: string
  category: StoryStatus
  color: string | null
  sort_order: number
  is_default: boolean
}

export async function listWorkflowStates(projectId: number): Promise<WorkflowState[]> {
  return api<WorkflowState[]>('/workflow-states', { query: { project_id: projectId } })
}
